import { useEffect, useRef } from "react";
import deepResearchStore from "Stores/deepResearchData";
import contactStoreData from "Stores/contactData";
import { getRecentProductEvents } from "Pages/Dashboard/Copilot/agent/copilotTelemetry";

// Per-page context adapters. A page registers a function returning
// { entity, selection, viewState } for what the user is currently looking
// at; unregistered pages degrade to route-level context only.
const adapters = new Map();

const ROUTE_KEYS = [
  ["/sequences", "sequences"],
  ["/contacts", "contacts"],
  ["/inbox", "inbox"],
  ["/calls", "calls"],
  ["/tasks", "tasks"],
  ["/reports", "reports"],
  ["/accounts", "accounts"],
  ["/list-builder", "list_builder"],
  ["/discover", "discover"],
  ["/deep-research", "deep_research"],
  ["/templates", "templates"],
  ["/settings", "settings"],
  ["/dashboard", "dashboard"],
  ["/data-toolkit", "data_toolkit"],
  ["/copilot", "copilot"]
];

// Routes that open one record. The id becomes page.entity and the surface
// gets a _detail suffix so insights are about that record.
export const ENTITY_ROUTES = [
  // Detail is any two-segment sequence URL: /sequences/all/<id>,
  // /sequences/shared/<id>, and folder-scoped /sequences/<folderId>/<id>.
  // One segment (/sequences/all or a bare folder) stays the list surface.
  ["sequences", "sequence", /^\/sequences\/[^/]+\/([^/]+)/],
  ["contacts", "contact", /^\/contacts\/([^/]+)\/(?:activity|deepresearch)/],
  ["accounts", "account", /^\/accounts\/([^/]+)/],
  ["templates", "template", /^\/templates\/(?!all$|shared$)([^/]+)$/]
];

const entityFor = (route, pathname) => {
  const hit = ENTITY_ROUTES.find(([r]) => r === route);
  const m = hit && pathname.match(hit[2]);
  return m ? { type: hit[1], id: m[1] } : null;
};

const MAX_SELECTION_IDS = 50;

const hasEntityRoute = route => ENTITY_ROUTES.some(([r]) => r === route);

// A "section" is what the receipt names: the route, or a specific entity
// when one is open. Same rule loadPageInsights uses to pick a surface, so the
// insights card and the receipt always agree about where you are.
export const sectionKeyOf = context => {
  const route = context?.page?.route || "";
  const entityId = context?.page?.entity?.id || "";
  return hasEntityRoute(route) && entityId ? `${route}:${entityId}` : route;
};

// The surface/entity pair the insights endpoint and the drawer both key on.
// Same rule as loadPageInsights, kept here so callers do not re-derive it.
export const currentSurface = () => {
  const context = getCopilotContext();
  const route = context?.page?.route || "";
  const entityId = context?.page?.entity?.id || "";
  // Settings is a dozen pages behind one route. It has no entity, so the
  // sub-page rides in the entity slot: it keys the insight cache per page and
  // lets the read be about CRM, or schedules, rather than "settings" at large.
  if (route === "settings") {
    return { surface: "settings", entityId: context?.viewState?.subPage || "" };
  }
  return {
    surface: hasEntityRoute(route) && entityId ? `${route}_detail` : route,
    entityId
  };
};

const SECTION_LABELS = {
  sequences: "Sequences",
  contacts: "Contacts",
  inbox: "Inbox",
  calls: "Calls",
  tasks: "Tasks",
  reports: "Reports",
  accounts: "Accounts",
  list_builder: "List builder",
  discover: "Discover",
  deep_research: "Deep research",
  templates: "Templates",
  settings: "Settings",
  dashboard: "Dashboard",
  data_toolkit: "Data toolkit"
};

export const sectionLabel = context => {
  const route = context?.page?.route || "";
  const base = SECTION_LABELS[route] || route.replace(/_/g, " ") || "This page";
  const entity = context?.page?.entity;
  // Name the sequence when we have it — "Sequences" twice in the receipt is
  // useless when they are two different sequences.
  if (entity?.name) return entity.name;
  const count = context?.selection?.total || context?.selection?.ids?.length;
  return count ? `${base} (${count})` : base;
};

export const registerCopilotContextAdapter = (routeKey, fn) => {
  adapters.set(routeKey, fn);
  return () => {
    if (adapters.get(routeKey) === fn) adapters.delete(routeKey);
  };
};

// Pages register through this hook: the ref keeps the adapter reading live
// state (selection lives in component-local useState) with a single
// registration per mount, so no dependency array can go stale.
export const useCopilotContext = (routeKey, fn, enabled = true) => {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    return registerCopilotContextAdapter(routeKey, () => ref.current());
  }, [routeKey, enabled]);
};

// Built-in adapter: contact rows carry a per-item `checked` flag on the
// contacts store — surface the checked ones as the selection.
adapters.set("contacts", () => {
  const rows = contactStoreData.contactData;
  if (!Array.isArray(rows)) return null;
  const checked = rows.filter(row => row?.checked && row?.id);
  if (!checked.length) return null;
  return {
    selection: {
      type: "contact",
      ids: checked.map(row => row.id),
      names: checked.slice(0, 10).map(row => row.name || row.email || "")
    }
  };
});

const routeKeyFor = pathname =>
  (ROUTE_KEYS.find(([prefix]) => pathname.startsWith(prefix)) || [])[1] ||
  "other";

// IDs only — the orchestrator re-fetches fresh data under the user's own
// auth, so nothing here is trusted as fact, only as pointers.
// The pop-out window and the full page live at /copilot, so the context they
// can see about themselves is empty -- they are not standing on any page. The
// main window mirrors its own context here so they can borrow it.
const MIRROR_KEY = "sg_copilot_context_mirror";

const readMirror = () => {
  try {
    return JSON.parse(localStorage.getItem(MIRROR_KEY) || "null");
  } catch (e) {
    return null;
  }
};

/**
 * Publish the current page's context for other windows. Called on navigation
 * from the main app only -- a copilot window has nothing to contribute and
 * must never overwrite the real page's context with its own emptiness.
 */
export const mirrorPageContext = () => {
  const context = buildCopilotContext();
  if (context?.page?.route === "copilot") return;
  try {
    localStorage.setItem(MIRROR_KEY, JSON.stringify(context));
  } catch (e) {
    /* private mode / quota -- the window just falls back to no context */
  }
};

export const getCopilotContext = () => {
  const context = buildCopilotContext();
  // Standing on /copilot: borrow whatever page the main window is showing, so
  // asking from the pop-out is the same as asking from the page itself.
  if (context?.page?.route === "copilot") {
    const mirrored = readMirror();
    if (mirrored?.page?.route) return mirrored;
  }
  return context;
};

const buildCopilotContext = () => {
  const pathname = window.location.pathname || "";
  const route = routeKeyFor(pathname);
  const context = { v: 1, page: { pathname, route } };
  const sellerProfileId = deepResearchStore.activeSellerProfileId;
  if (sellerProfileId) {
    context.sellerProfileId = sellerProfileId;
  }
  // Built-in: entity id straight from the URL — the orchestrator resolves
  // id → name/stats itself.
  const builtIn = entityFor(context.page.route, context.page.pathname);
  if (builtIn) context.page.entity = builtIn;
  const adapter = adapters.get(route);
  if (adapter) {
    try {
      const { entity, selection, viewState } = adapter() || {};
      if (entity?.id) context.page.entity = entity;
      if (selection?.ids?.length) {
        const { rows, ...rest } = selection;
        context.selection = {
          ...rest,
          ids: selection.ids.slice(0, MAX_SELECTION_IDS),
          total: selection.ids.length
        };
      }
      if (viewState) context.viewState = viewState;
    } catch (e) {
      console.error("copilot context adapter failed", e);
    }
  }
  const recentEvents = getRecentProductEvents();
  if (recentEvents.length) context.recentEvents = recentEvents;
  return context;
};
